import { SectionTypes } from "../../../../resources/modelTypes";

/** Amount of viewport left uncovered when the panel is at its tallest. */
const FULL_HEIGHT_INSET = 100;

export const getViewportHeight = (): number =>
  typeof window === "undefined" ? 0 : window.innerHeight;

/**
 * How tall a panel opens, relative to the viewport.
 */
export enum PanelHeightTier {
  Short = "short",
  Half = "half",
  Full = "full",
}

type TierHeight = {
  minHeight?: number;
  ratio: number;
};

const TIER_HEIGHTS: Record<PanelHeightTier, TierHeight> = {
  [PanelHeightTier.Short]: {
    minHeight: 365,
    ratio: 0.4,
  },
  [PanelHeightTier.Half]: {
    minHeight: 420,
    ratio: 0.6,
  },
  [PanelHeightTier.Full]: {
    ratio: 1,
  },
};

export const getPanelMaxHeight = (): number =>
  getViewportHeight() - FULL_HEIGHT_INSET;

/**
 * Enum representing all possible SwipePanel views in the mobile experience.
 * This includes both content sections and design/settings views.
 */
export enum SwipePanelView {
  // Content sections (from the Add Section list)
  Button = "Button",
  CashRegistry = "CashRegistry",
  Comments = "Comments",
  EventNameDescription = "EventNameDescription",
  Gallery = "Gallery",
  Image = "Image",
  ImageAndText = "ImageAndText",
  Registry = "Registry",
  Text = "Text",
  WebsiteURL = "WebsiteURL",
  Event = "event",

  // Design/Settings views
  EditColors = "EditColors",
  EditDesign = "EditDesign",
  EditFonts = "EditFonts",
}

/**
 * Height tier for each SwipePanel view
 */
export const PANEL_HEIGHT_TIERS: Record<SwipePanelView, PanelHeightTier> = {
  // Content sections - short height
  [SwipePanelView.Text]: PanelHeightTier.Short,
  [SwipePanelView.WebsiteURL]: PanelHeightTier.Short,
  [SwipePanelView.Button]: PanelHeightTier.Short,
  [SwipePanelView.Image]: PanelHeightTier.Short,

  // Content sections - half height
  [SwipePanelView.EventNameDescription]: PanelHeightTier.Half,
  [SwipePanelView.Registry]: PanelHeightTier.Half,
  [SwipePanelView.ImageAndText]: PanelHeightTier.Half,

  // Content sections - full height for complex forms
  [SwipePanelView.CashRegistry]: PanelHeightTier.Full,
  [SwipePanelView.Comments]: PanelHeightTier.Full, // Technically unused
  [SwipePanelView.Gallery]: PanelHeightTier.Full,
  [SwipePanelView.Event]: PanelHeightTier.Full,

  // Design views
  [SwipePanelView.EditColors]: PanelHeightTier.Short,
  [SwipePanelView.EditDesign]: PanelHeightTier.Half,
  [SwipePanelView.EditFonts]: PanelHeightTier.Half,
};

export const getPanelHeight = (view: SwipePanelView): number => {
  const { minHeight = 0, ratio } = TIER_HEIGHTS[PANEL_HEIGHT_TIERS[view]];

  return Math.round(
    Math.min(
      getPanelMaxHeight(),
      Math.max(minHeight, getViewportHeight() * ratio),
    ),
  );
};

/**
 * Maps SectionTypes to SwipePanelView for content sections
 */
export const SECTION_TYPE_TO_PANEL_VIEW: Partial<
  Record<SectionTypes, SwipePanelView>
> = {
  [SectionTypes.Anchor]: SwipePanelView.WebsiteURL,
  [SectionTypes.Button]: SwipePanelView.Button,
  [SectionTypes.CashRegistry]: SwipePanelView.CashRegistry,
  [SectionTypes.Comments]: SwipePanelView.Comments,
  [SectionTypes.Gallery]: SwipePanelView.Gallery,
  [SectionTypes.ImageAndText]: SwipePanelView.ImageAndText,
  [SectionTypes.Media]: SwipePanelView.Image,
  [SectionTypes.Registry]: SwipePanelView.Registry,
  [SectionTypes.Text]: SwipePanelView.Text,
  [SectionTypes.Event__FAKE]: SwipePanelView.Event,
};

/**
 * Converts a SectionType to a SwipePanelView
 * Defaults to Text panel view if no mapping exists
 */
export const sectionTypeToSwipePanelView = (
  sectionType: SectionTypes,
): SwipePanelView => {
  return SECTION_TYPE_TO_PANEL_VIEW[sectionType] ?? SwipePanelView.Text;
};
